import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img40 from "../../../assets/img40.jpg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function CriticalIllnessInsurance() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";

  return (
    <>
      <SEO
        title="Assurance maladies graves au Canada – einsured.ca"
        description="Comparez les assurances maladies graves au Canada pour protéger vos finances contre les frais médicaux. Obtenez des soumissions en ligne ou des conseils d’experts."
        keywords="Assurance maladies graves Canada, couverture maladie grave, assurance cancer, assurance crise cardiaque canada"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img40})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className={`relative z-10 text-white w-full ${container}`}>
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Assurance maladies graves
            </h1>

            <p className="text-base sm:text-lg md:text-xl font-semibold mb-8">
              Assurance pour couvrir les maladies mettant la vie en danger
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* INTRO SECTION */}
      <section className="py-16 sm:py-20 md:py-20 bg-white">
        <div className={`w-full ${container}`}>
          <div className="max-w-5xl mx-auto text-gray-800">
            <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#49628b] text-center mb-10">
              Assurance maladies graves
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              <span className="text-[#00a8ff] font-semibold">
                Assurance maladies graves
              </span>{" "}
              - Assurance qui vous fournit une couverture financière lorsque vous
              êtes diagnostiqué avec une maladie mettant la vie en danger.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              Lorsque vous entendez parler de quelqu'un qui a tout perdu en raison
              d'une maladie grave, cela est généralement causé par l'une des{" "}
              <span className="text-[#20a7df] underline">
                25 maladies les plus fréquentes et graves
              </span>
              . Si l'une de ces maladies est contractée, une hospitalisation
              prolongée et/ou un traitement médical intensif peut être nécessaire.
              En fin de compte, elles constituent une menace pour la vie et
              nécessitent des ressources médicales considérables.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8">
              Les coûts de ces ressources peuvent s'accumuler rapidement. Vous
              pourriez être responsable de milliers de dollars en frais médicaux
              qui peuvent augmenter considérablement chaque jour. Cela pourrait
              être dévastateur pour vous et votre famille. Cela peut anéantir vos
              économies, vos fonds de retraite, vous forcer à vendre vos biens
              précieux et vous endetter pendant des années.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT'S THE SOLUTION */}
      <section className="py-16 sm:py-20 md:py-2 bg-white">
        <div className={`w-full ${container}`}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              Quelle est la solution ?
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-6">
              <span className="text-[#20a7df] underline">
                L'assurance maladies graves
              </span>{" "}
              vous verse une importante prestation forfaitaire non imposable si vous
              êtes diagnostiqué avec l'une des maladies mettant la vie en danger
              mentionnées dans votre contrat. De plus, l'assurance maladies graves
              offrira également à votre famille et à vos proches une sécurité
              financière pour continuer à poursuivre leurs rêves.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              Cela vous aide à payer les dépenses médicales pendant votre
              rétablissement et votre incapacité à travailler. Cela vous protège
              également contre le résultat malheureux qui force le plus souvent les
              individus et les familles à déclarer faillite. Les frais médicaux
              croissants et la perte de travail ne seront pas un grand défi tant
              que vous avez souscrit la bonne{" "}
              <span className="text-[#00a8ff] font-semibold">
                assurance maladies graves au Canada
              </span>{" "}
              recommandée par einsured.ca, avec la possibilité de maintenir le même
              style de vie lors d'un diagnostic de maladie grave couverte.
            </p>

            <p className="text-base sm:text-lg mb-4">
              Les caractéristiques clés de l'assurance maladies graves :
            </p>

            <ul className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  Une police qui vous offre un filet de sécurité financier après
                  la fin du délai d'attente
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  Vous recevez un montant forfaitaire sous forme de paiements en
                  espèces pour couvrir les dépenses qui ne sont généralement pas
                  couvertes par votre carte santé
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-[#00b4ff] mt-1 shrink-0" />
                <p>
                  Un dépôt direct sur le compte bancaire de l'assuré plutôt qu'un
                  remboursement des frais médicaux
                </p>
              </li>
            </ul>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-4">
              Si vous le souhaitez, vous pouvez utiliser la prestation reçue pour
              prendre en charge d'autres obligations financières en plus de payer
              les frais liés à la maladie. Cet argent est à votre disposition !
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-10">
              Nos experts en assurance vous aideront à choisir le meilleur régime
              d'assurance maladies graves adapté à vos besoins et à votre budget.
            </p>

            <div className="flex justify-center">
              <Link
                to="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Protégez-vous !
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Significance of critical illness insurance for all age groups */}
      <section
        className="relative py-20 mt-16 sm:mt-24 sm:py-24 md:py-20 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              L'importance de l'assurance maladies graves pour tous les groupes d'âge
            </h2>

            <p className="text-base sm:text-lg leading-relaxed max-w-5xl mx-auto mb-4 text-left sm:text-center">
              <span className="underline">L'assurance maladies graves</span> est
              essentielle pour les individus de tous les groupes d'âge au Canada
              pour plusieurs raisons. Ce type d'assurance offre une protection
              financière en cas de maladie grave, en versant une prestation
              forfaitaire lors du diagnostic d'une condition couverte.
            </p>

            <p className="text-lg sm:text-xl font-semibold max-w-5xl mx-auto mb-10 text-left sm:text-center">
              Voici les principales raisons pour lesquelles l'assurance maladies
              graves est essentielle pour les individus de tous âges :
            </p>

            <div className="grid gap-6 md:gap-8 md:grid-cols-2 text-left">
              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Sécurité financière pendant la convalescence :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Une maladie grave peut entraîner des dépenses médicales
                  substantielles, et la convalescence peut impliquer des coûts
                  supplémentaires tels que la réadaptation, les médicaments et les
                  aménagements du domicile. L'assurance maladies graves verse une
                  prestation forfaitaire qui peut être utilisée pour couvrir ces
                  dépenses et maintenir la stabilité financière pendant la période
                  de rétablissement.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Remplacement du revenu :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Pour les personnes actives, une maladie grave peut entraîner une
                  incapacité temporaire ou permanente de travailler. La prestation
                  forfaitaire de l'assurance maladies graves peut servir de source
                  de remplacement du revenu, aidant à couvrir les dépenses
                  quotidiennes, les versements hypothécaires ou de loyer, et autres
                  obligations financières.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Couverture des dépenses non médicales :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Les fonds de l'assurance maladies graves ne sont pas limités aux
                  dépenses médicales. Les individus peuvent utiliser la prestation
                  forfaitaire pour couvrir les coûts non médicaux, tels que la
                  garde d'enfants, le transport ou les aménagements liés aux
                  changements de la vie quotidienne causés par la maladie.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Lacunes dans les régimes provinciaux de santé :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Bien que le système de santé du Canada couvre les traitements
                  médicaux, il existe souvent des lacunes que les individus doivent
                  combler, telles que les traitements expérimentaux, les
                  déplacements pour des soins spécialisés ou les thérapies
                  alternatives. L'assurance maladies graves comble ces lacunes,
                  permettant aux individus d'accéder aux meilleurs traitements
                  disponibles sans contraintes financières.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Soutien aux personnes à charge :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  L'assurance maladies graves offre un soutien financier aux
                  personnes à charge, leur permettant de maintenir leur niveau de
                  vie même si le principal soutien de famille est incapable de
                  travailler en raison d'une maladie grave. C'est particulièrement
                  crucial pour les familles avec enfants ou d'autres personnes à
                  charge.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Remboursement des dettes :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  La prestation forfaitaire de l'assurance maladies graves peut
                  être utilisée pour rembourser les dettes en cours, telles que les
                  hypothèques, les prêts ou les soldes de cartes de crédit. Cette
                  assurance contre les maladies graves aide à prévenir
                  l'accumulation de dettes pendant une période de maladie et permet
                  aux individus de se concentrer sur leur rétablissement sans stress
                  financier.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Tranquillité d'esprit à chaque étape de la vie :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Une maladie grave peut survenir à tout âge. Avoir une couverture
                  d'assurance procure une tranquillité d'esprit aux individus et à
                  leurs familles, sachant qu'ils sont protégés financièrement face à
                  un diagnostic de santé grave. Cela leur permet de se concentrer
                  sur leur rétablissement plutôt que de s'inquiéter des
                  répercussions financières d'une maladie grave.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Primes abordables à un jeune âge :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  Les primes d'assurance maladies graves sont souvent plus
                  abordables lorsqu'elles sont souscrites à un jeune âge et en
                  bonne santé. Commencer la couverture tôt permet aux individus de
                  bloquer des primes moins élevées et de bénéficier d'une
                  protection complète contre les maladies graves potentielles qui
                  pourraient survenir plus tard dans la vie.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Protection contre l'augmentation des maladies graves :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  L'incidence des maladies graves, telles que le cancer, les
                  maladies cardiaques et les accidents vasculaires cérébraux, est en
                  hausse. L'assurance maladies graves répond à l'évolution du
                  paysage de la santé et offre un filet de sécurité financier face à
                  ces conditions répandues et impactantes.
                </p>
              </div>

              <div className="bg-white/95 rounded-3xl p-6 sm:p-8 shadow-lg text-gray-800">
                <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                  Flexibilité et choix :
                </h3>
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="underline">L'assurance maladies graves</span>{" "}
                  offre de la flexibilité, permettant aux individus de choisir le
                  montant de couverture et la durée qui correspondent le mieux à
                  leurs besoins. Cette personnalisation garantit que la police
                  s'aligne sur les objectifs financiers et les circonstances
                  individuelles.
                </p>
              </div>
            </div>

            <p className="mt-12 text-base sm:text-lg leading-relaxed text-gray-100 max-w-5xl mx-auto text-left sm:text-center">
              En résumé, l'assurance maladies graves est essentielle pour les
              individus de tous les groupes d'âge au Canada, car elle constitue un
              filet de sécurité financier pendant une période difficile de maladie
              grave. Elle permet aux individus de se concentrer sur leur
              rétablissement sans le fardeau supplémentaire du stress financier, en
              faisant un élément intégral d'un plan financier global pour les
              individus et les familles.
            </p>
          </div>
        </div>
      </section>

      {/* 25+ Critical Illnesses */}
      <section className="py-16 sm:py-20 md:py-24 bg-white">
        <div className={`w-full ${container}`}>
          <div className="max-w-6xl mx-auto text-gray-800">
            <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#49628b] text-center mb-8">
              L'assurance maladies graves couvre 25 maladies graves ou plus
            </h2>

            <div className="space-y-4 text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              <p>
                Les{" "}
                <span className="text-[#00b4ff] font-semibold">
                  maladies graves couvertes par l'assurance maladies graves
                </span>{" "}
                peuvent varier selon les assureurs, et les polices peuvent différer
                en termes de nombre et de types de maladies couvertes.
              </p>
              <p>
                Cependant, il existe des maladies graves communes qui sont
                généralement incluses dans de nombreuses polices d'assurance
                maladies graves au Canada. Voici une liste générale de{" "}
                <span className="text-[#00b4ff] font-semibold">
                  25 maladies graves souvent couvertes :
                </span>
              </p>
            </div>

            <div className="mt-6 space-y-4 text-base sm:text-lg leading-7 sm:leading-8">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">Cancer :</span>{" "}
                  Cancer mettant la vie en danger diagnostiqué par un médecin qualifié.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Crise cardiaque :
                  </span>{" "}
                  Infarctus du myocarde entraînant la mort du tissu musculaire
                  cardiaque.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Accident vasculaire cérébral :
                  </span>{" "}
                  Incident cérébrovasculaire causant un infarctus du tissu cérébral.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Pontage coronarien :
                  </span>{" "}
                  Intervention chirurgicale pour corriger le rétrécissement ou le
                  blocage des artères coronaires.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Chirurgie de l'aorte :
                  </span>{" "}
                  Remplacement ou réparation chirurgicale de l'aorte en raison d'une
                  maladie ou d'un traumatisme.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Insuffisance rénale :
                  </span>{" "}
                  Insuffisance rénale terminale nécessitant une dialyse régulière ou
                  une greffe de rein.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Transplantation d'organe majeur :
                  </span>{" "}
                  Transplantation d'un organe vital, tel que le cœur, le poumon, le
                  foie, le pancréas ou la moelle osseuse.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Paralysie :
                  </span>{" "}
                  Perte totale et irréversible de l'usage de deux membres ou plus.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Sclérose en plaques :
                  </span>{" "}
                  Diagnostic définitif de sclérose en plaques causant des déficits
                  neurologiques spécifiques.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Maladie d'Alzheimer :
                  </span>{" "}
                  Détérioration irréversible des facultés mentales due à la maladie
                  d'Alzheimer.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Maladie de Parkinson :
                  </span>{" "}
                  Maladie de Parkinson diagnostiquée par un neurologue.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Perte de la parole :
                  </span>{" "}
                  Perte totale et irréversible de la parole à la suite d'une maladie
                  ou d'une blessure.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">Surdité :</span>{" "}
                  Perte totale et irréversible de l'ouïe dans les deux oreilles.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">Cécité :</span>{" "}
                  Perte totale et irréversible de la vue dans les deux yeux.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Brûlures graves :
                  </span>{" "}
                  Brûlures couvrant un pourcentage spécifié de la surface corporelle.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Tumeur cérébrale bénigne :
                  </span>{" "}
                  Présence d'une tumeur non maligne dans le cerveau nécessitant une
                  intervention chirurgicale.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">Coma :</span>{" "}
                  État d'inconscience provoqué médicalement ou survenant naturellement.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Perte de membres :
                  </span>{" "}
                  Perte totale et irréversible de l'usage de deux membres ou plus.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Maladie du neurone moteur :
                  </span>{" "}
                  Diagnostic d'une maladie du neurone moteur spécifiée.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Méningite bactérienne :
                  </span>{" "}
                  Diagnostic de méningite bactérienne avec des symptômes spécifiques.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Remplacement ou réparation de valvule cardiaque :
                  </span>{" "}
                  Intervention chirurgicale pour remplacer ou réparer les valvules
                  cardiaques.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Défaillance d'organe majeur en liste d'attente :
                  </span>{" "}
                  Être sur une liste d'attente pour une transplantation d'organe
                  admissible.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Perte d'autonomie :
                  </span>{" "}
                  Perte totale et irréversible de la capacité à effectuer les
                  activités de la vie quotidienne.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Infection au VIH en milieu professionnel :
                  </span>{" "}
                  Contracter le VIH lors d'un accident au travail.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 mt-1 text-[#00b4ff] shrink-0" />
                <p>
                  <span className="font-semibold text-[#49628b]">
                    Perte de fonction d'un membre :
                  </span>{" "}
                  Perte totale et irréversible de l'usage d'un membre.
                </p>
              </div>
            </div>

            <div className="mt-10 space-y-4 text-base sm:text-lg leading-7 sm:leading-8 text-gray-800">
              <p>
                Il est important de noter que les{" "}
                <span className="underline">
                  modalités, conditions et définitions
                </span>{" "}
                de la police peuvent varier, et les individus doivent examiner
                attentivement leur police spécifique pour comprendre la couverture
                fournie.
              </p>
              <p>
                De plus, la liste ci-dessus est illustrative, et la couverture réelle
                peut varier selon la compagnie d'assurance et les détails de la police.
              </p>
              <p>
                Consultez l'un de nos conseillers autorisés pour{" "}
                <Link
                  to="/fr/devis/vtc"
                  className="text-[#00b4ff] font-semibold underline"
                >
                  obtenir votre soumission
                </Link>{" "}
                et souscrire une{" "}
                <span className="text-[#00b4ff] font-semibold">
                  assurance vie avec maladies graves.
                </span>
              </p>
            </div>

            <div className="mt-10 flex justify-center">
              <Link
                to="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Obtenez votre meilleur taux
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDY - JOHN'S JOURNEY */}
      <section className="relative pt-20 sm:pt-24 md:pt-22 pb-16 sm:pb-20 md:pb-24 bg-[#dce2ea]">
        <div className="absolute -top-12 left-0 w-full h-12 bg-white"></div>
        <div className="absolute -top-[4px] left-0 w-full h-[4px] bg-[#2b4672]"></div>
        <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#2b4672]"></div>

        <div className={`relative w-full ${container}`}>
          <div className="max-w-6xl mx-auto text-[#27406b]">
            <h2 className="text-[#49628b] text-3xl sm:text-4xl md:text-4xl font-bold text-center mb-6">
              Étude de cas : Le parcours de Jean sans assurance maladies graves
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-4 text-left sm:text-center">
              Rencontrez Jean, un professionnel de 45 ans qui, comme beaucoup
              d'autres, croyait être invincible face aux défis de santé.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-10 text-left sm:text-center">
              Malheureusement, la vie en avait décidé autrement. Cette étude de cas
              explore le parcours de Jean après avoir reçu un diagnostic de cancer
              en l'absence d'assurance maladies graves.
            </p>

            <h3 className="text-[#49628b] text-2xl sm:text-3xl font-bold mb-3">
              Contexte :
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              Jean était un directeur marketing accompli, menant une vie bien
              remplie avec des engagements professionnels, des responsabilités
              familiales et des activités personnelles. Malgré une conscience aiguë
              de sa santé et un mode de vie actif, il n'avait jamais envisagé la
              nécessité d'une assurance maladies graves.
            </p>

            <h3 className="text-[#49628b] text-2xl sm:text-3xl font-bold mb-3">
              Diagnostic :
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-12">
              Lors d'un examen de routine, le médecin de Jean a découvert des
              anomalies qui ont conduit à des investigations supplémentaires. À sa
              grande surprise, Jean a reçu un diagnostic de cancer colorectal de
              stade II. La nouvelle était accablante pour lui et sa famille, qui
              faisaient face non seulement aux défis émotionnels et physiques, mais
              aussi aux implications financières de traitements médicaux intensifs.
            </p>

            <h3 className="text-[#49628b] text-2xl sm:text-3xl md:text-3xl font-bold text-center mb-10">
              Impact financier :
            </h3>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold text-center mb-4">
                  Frais médicaux :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Sans assurance maladies graves, Jean a dû se fier uniquement à sa
                  couverture santé existante, qui ne couvrait pas tous les aspects
                  du traitement du cancer. La chimiothérapie, la chirurgie et les
                  traitements de suivi ont entraîné d'importantes dépenses à sa
                  charge.
                </p>
              </div>

              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold text-center mb-4">
                  Perte de revenu :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  En tant que principal soutien de famille, l'incapacité de Jean à
                  travailler pendant les traitements signifiait une perte soudaine
                  de revenu. La pression financière sur la famille s'est intensifiée,
                  affectant leur capacité à couvrir les dépenses quotidiennes, les
                  versements hypothécaires et les frais d'éducation de leurs enfants.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold text-center mb-4">
                  Épuisement des économies :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Jean a dû puiser dans ses économies pour couvrir les factures
                  médicales et les dépenses courantes. L'épuisement des économies
                  signifiait compromettre les objectifs financiers futurs, notamment
                  les études des enfants et les plans de retraite.
                </p>
              </div>

              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold text-center mb-4">
                  Fardeau émotionnel :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Le stress financier a ajouté une couche supplémentaire de fardeau
                  à une situation déjà émotionnellement difficile. Jean avait du mal
                  à se concentrer sur son rétablissement tout en s'inquiétant
                  constamment de la stabilité financière de sa famille.
                </p>
              </div>
            </div>

            <h3 className="text-[#49628b] text-2xl sm:text-3xl md:text-3xl font-bold text-center mb-10">
              Leçons apprises :
            </h3>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold mb-4">
                  Importance de la protection financière :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  L'expérience de Jean a mis en lumière l'importance de l'{" "}
                  <span className="text-[#00b4ff] font-semibold">
                    assurance vie avec maladies graves
                  </span>
                  . Un filet de sécurité sous forme d'assurance maladies graves
                  aurait pu alléger ce fardeau, lui permettant de se concentrer sur
                  son rétablissement plutôt que sur ses soucis financiers.
                </p>
              </div>

              <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10">
                <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold mb-4">
                  La planification précoce est importante :
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Jean a réalisé qu'une planification précoce et la prise en compte
                  de l'assurance maladies graves auraient pu faire une différence
                  significative. La possibilité d'obtenir une couverture à un jeune
                  âge et en bonne santé lui aurait fourni une protection abordable
                  et complète.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10 mb-8">
              <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold mb-4">
                Après le rétablissement :
              </h4>
              <p className="text-base sm:text-lg leading-7 sm:leading-8">
                Malgré les défis, Jean a réussi à suivre son traitement et est entré
                en rémission. Cependant, les répercussions financières ont continué
                même après le rétablissement, affectant la stabilité financière à
                long terme de la famille.
              </p>
            </div>

            <div className="bg-white rounded-[32px] shadow-lg shadow-slate-300/60 px-8 sm:px-10 py-8 sm:py-10 mb-10">
              <h4 className="text-[#49628b] text-xl sm:text-2xl font-bold mb-4">
                Aller de l'avant :
              </h4>
              <p className="text-base sm:text-lg leading-7 sm:leading-8">
                Jean, ayant tiré les leçons de cette expérience, est maintenant un
                défenseur de l'assurance maladies graves. Il encourage ses amis, sa
                famille et ses collègues à considérer la protection financière
                qu'elle offre, soulignant l'importance d'une planification précoce
                et de la souscription d'une couverture pour atténuer l'impact des
                défis de santé imprévus.
              </p>
            </div>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8 text-left sm:text-center">
              Cette étude de cas rappelle de manière poignante que les
              incertitudes en matière de santé peuvent toucher n'importe qui.
              L'assurance maladies graves n'est pas seulement un produit financier ;
              c'est un filet de sécurité contre l'imprévu, offrant tranquillité
              d'esprit et protection dans les moments les plus difficiles de la vie.
              Pensez toujours à souscrire une{" "}
              <span className="text-[#00b4ff] font-semibold">
                assurance vie avec maladies graves !
              </span>
            </p>

            <div className="flex justify-center">
              <Link
                to="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Sécurisez votre revenu maintenant
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
